import React, { useLayoutEffect, useRef, useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  MousePointer2, Square, Circle, Type, Play, Pause,
  Undo2, Redo2, Trash, ChevronLeft, ChevronRight,
  Pencil, PenTool, Minus, Triangle, Star, Settings2, RotateCcw, LocateFixed,
  ArrowRight,
} from "lucide-react";

const COLLAPSE_DURATION = 0.42; // width fold (accordion)
const SLIDE_DURATION = 0.36;    // slide-to-edge + capsule morph
const BTN_W = 40;               // collapse button width (expanded)
const CAPSULE_W = 64;           // collapse button width (collapsed pill)
const REVEAL = CAPSULE_W / 2;   // how much of the capsule stays visible
const EASE_FOLD = [0.4, 0, 0.2, 1];
const EASE_SLIDE = [0.22, 1, 0.36, 1];

function ToolButton({ active, testId, title, onClick, icon: Icon, disabled }) {
  return (
    <button
      title={title}
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={`h-9 w-9 shrink-0 flex items-center justify-center transition-colors ${
        active
          ? "bg-[#FF6B00] text-white"
          : "text-ink2 hover:bg-hover hover:text-ink"
      } disabled:opacity-30 disabled:hover:bg-transparent`}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

export default function FloatingToolbar({
  tool, setTool,
  playing, togglePlay,
  onReplay,
  onUndo, onRedo, canUndo, canRedo,
  onClear,
  collapsed, toggleCollapsed,
  onOpenSettings,
  onRecenter,
}) {
  const stripRef = useRef(null);
  const scrollRef = useRef(null);
  const panelRef = useRef(null);
  // Cache the last measured values so we can skip redundant setState calls
  // during rapid resize / panel transitions (ResizeObserver can fire many
  // times per frame). Only when a value actually changes do we update state.
  const lastMeasure = useRef({ cw: 0, stripW: 0, right: 16 });
  // rAF handle for throttling resize-driven re-measures to one per frame.
  const rafMeasure = useRef(0);
  const [contentW, setContentW] = useState(0);
  // The visible strip width — clamped to the available space so on narrow
  // viewports the toolbar fits with equal side margins and scrolls its
  // content horizontally instead of overflowing the canvas.
  const [stripW, setStripW] = useState(0);
  const [rightExpanded, setRightExpanded] = useState(16);
  const [masking, setMasking] = useState(false);
  // True only during a collapse/expand toggle, while the docking slide tween
  // should play. When false, `right` tracks the canvas edge instantly so the
  // toolbar stays glued to centre as the side panels animate (no lag/jitter).
  const [sliding, setSliding] = useState(false);
  // True after the first measurement so the initial centring is instant (no
  // entrance glide); afterwards the collapse/expand `right` transition runs.
  const [animReady, setAnimReady] = useState(false);

  // Arm the docking tween *synchronously* in the same render that `collapsed`
  // flips. If we only set this in an effect (which runs after paint), the first
  // committed frame would apply the new `right` value with the instant
  // ("right 0s") transition, snapping the bar to the edge before the strip has
  // folded — i.e. collapsing and docking at the same time. Setting it during
  // render guarantees the delayed dock transition is in place when `right`
  // changes, so the bar folds fully first, then slides to the edge.
  const prevCollapsed = useRef(collapsed);
  if (prevCollapsed.current !== collapsed) {
    prevCollapsed.current = collapsed;
    setSliding(true);
  }

  // Core measurement — pure, no state. Returns the three values the toolbar
  // needs: content width, clamped visible strip width, and the `right` offset
  // that centres the panel. Kept separate so callers can throttle/batch.
  const compute = useCallback(() => {
    const strip = stripRef.current;
    const panel = panelRef.current;
    if (!strip || !panel) return null;
    const cw = strip.scrollWidth;
    const parent = panel.offsetParent || panel.parentElement;
    const parentW = parent ? parent.clientWidth : window.innerWidth;
    // Available width for the strip = parent minus the collapse button, borders,
    // and equal side margins (16px each side = 32). When the content is wider
    // than this, the strip clamps to the available width and scrolls inside.
    const MARGIN = 16;
    const maxStripW = Math.max(0, parentW - BTN_W - 2 - MARGIN * 2);
    const visibleW = Math.min(cw, maxStripW);
    const panelW = visibleW + BTN_W + 2;
    const right = Math.max(MARGIN, (parentW - panelW) / 2);
    return { cw, stripW: visibleW, right };
  }, []);

  // Apply measured values to state, but only when they actually change. This
  // avoids redundant re-renders during rapid ResizeObserver bursts (the canvas
  // section fires many entries while side panels animate open/closed).
  const applyMeasure = useCallback(() => {
    const m = compute();
    if (!m) return;
    const last = lastMeasure.current;
    let changed = false;
    if (m.cw !== last.cw) { setContentW(m.cw); changed = true; }
    if (m.stripW !== last.stripW) { setStripW(m.stripW); changed = true; }
    if (m.right !== last.right) { setRightExpanded(m.right); changed = true; }
    if (changed) lastMeasure.current = m;
  }, [compute]);

  // Synchronous measure for the initial layout pass (useLayoutEffect) so the
  // first paint already has the correct width — no flash of a zero-width strip.
  const measure = useCallback(() => {
    const m = compute();
    if (!m) return;
    lastMeasure.current = m;
    setContentW(m.cw);
    setStripW(m.stripW);
    setRightExpanded(m.right);
  }, [compute]);

  // rAF-throttled re-measure for resize / ResizeObserver callbacks. Collapses
  // multiple notifications in a single frame into one measure + one render.
  const scheduleMeasure = useCallback(() => {
    if (rafMeasure.current) return;
    rafMeasure.current = requestAnimationFrame(() => {
      rafMeasure.current = 0;
      applyMeasure();
    });
  }, [applyMeasure]);

  useLayoutEffect(() => {
    measure();
    window.addEventListener("resize", scheduleMeasure);
    // The toolbar centres itself within the canvas section. That section grows
    // and shrinks when side panels collapse/expand (no window resize fires), so
    // observe it directly to keep the toolbar centred throughout the transition.
    // scheduleMeasure rAF-throttles the burst so only one re-render happens per
    // animation frame — smooth and cheap even during a 0.4s panel transition.
    const parent = panelRef.current?.offsetParent || panelRef.current?.parentElement;
    let ro;
    if (parent && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => scheduleMeasure());
      ro.observe(parent);
    }
    return () => {
      window.removeEventListener("resize", scheduleMeasure);
      if (rafMeasure.current) cancelAnimationFrame(rafMeasure.current);
      ro?.disconnect();
    };
  }, [measure, scheduleMeasure]);

  // Enable the left-edge fade mask only while folding / when collapsed,
  // so the leftmost icon stays crisp in the resting expanded state.
  useEffect(() => {
    setMasking(true);
    const t = setTimeout(
      () => { setMasking(false); setSliding(false); },
      (COLLAPSE_DURATION + SLIDE_DURATION) * 1000 + 120
    );
    return () => clearTimeout(t);
  }, [collapsed]);

  // Translate vertical mouse-wheel into horizontal scrolling on the strip, so
  // desktop users can scroll the overflowed toolbar with a standard wheel
  // gesture (trackpads already send horizontal deltas which work natively).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      // Only intercept when there is horizontal overflow to scroll.
      if (el.scrollWidth <= el.clientWidth) return;
      // If the user is rolling the wheel vertically (most common), convert it
      // to a horizontal delta. Horizontal trackpad deltas pass through as-is.
      if (e.deltaY !== 0 && e.deltaX === 0) {
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Arm the slide transition only after the first layout pass, so the initial
  // centring snaps into place instead of gliding in from the edge on mount.
  useEffect(() => {
    const id = requestAnimationFrame(() => setAnimReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const showMask = masking || collapsed;

  // While collapsed, the toolbar is docked to the canvas's right edge purely via
  // CSS `right` (a constant offset), so as the side panel collapses/expands and
  // the canvas edge moves, the docked toolbar follows it in perfect sync — no
  // JS re-measure, no transform tween, no lag. The slide-to-edge / slide-back
  // is a CSS transition on `right`; when collapsing it is delayed by the fold
  // duration so the strip fully folds *in place* first, then the capsule slides
  // out to the edge (fold → then dock).
  const rightTransition = collapsed
    ? `right ${SLIDE_DURATION}s cubic-bezier(0.22,1,0.36,1) ${COLLAPSE_DURATION}s`
    : `right ${SLIDE_DURATION}s cubic-bezier(0.22,1,0.36,1)`;

  const panelTransition = collapsed
    ? { borderRadius: { delay: COLLAPSE_DURATION, duration: SLIDE_DURATION, ease: EASE_SLIDE } }
    : { borderRadius: { duration: SLIDE_DURATION, ease: EASE_SLIDE } };

  // The strip width transition only plays the fold/unfold animation during a
  // collapse/expand toggle (when `sliding` is armed). For responsive width
  // changes (resize / panel transitions) the width updates instantly with no
  // delay — otherwise the 0.36s `delay: SLIDE_DURATION` would keep the strip
  // at its old width for a third of a second on every resize, making the
  // responsive clamp feel laggy instead of real-time.
  const stripTransition = sliding
    ? (collapsed
        ? { width: { duration: COLLAPSE_DURATION, ease: EASE_FOLD } }
        : { width: { delay: SLIDE_DURATION, duration: COLLAPSE_DURATION, ease: EASE_FOLD } })
    : { width: { duration: 0 } };

  const btnTransition = collapsed
    ? { width: { delay: COLLAPSE_DURATION, duration: SLIDE_DURATION, ease: EASE_SLIDE },
        paddingLeft: { delay: COLLAPSE_DURATION, duration: SLIDE_DURATION } }
    : { width: { duration: SLIDE_DURATION, ease: EASE_SLIDE },
        paddingLeft: { duration: SLIDE_DURATION } };

  return (
    <motion.div
      ref={panelRef}
      data-testid="floating-toolbar"
      className="floating-toolbar absolute top-4 flex items-center bg-panel-glass backdrop-blur border border-line shadow-2xl z-20 overflow-hidden"
      style={{
        right: collapsed ? -REVEAL : rightExpanded,
        transition: animReady && sliding ? rightTransition : "right 0s",
        // CSS safety net: even if a JS measure catches a mid-transition
        // parentWidth (side panels animating), the panel can never overflow
        // the canvas section. The JS clamp is the primary mechanism; this
        // guarantees no visual spill during rapid resize bursts.
        maxWidth: collapsed ? undefined : "calc(100% - 32px)",
      }}
      initial={false}
      animate={{ borderRadius: collapsed ? 999 : 12 }}
      transition={panelTransition}
    >
      <motion.div
        className="relative h-9 overflow-hidden"
        initial={false}
        animate={{ width: collapsed ? 0 : stripW }}
        transition={stripTransition}
        style={
          showMask
            ? {
                WebkitMaskImage: "linear-gradient(to right, transparent 0, #000 30px)",
                maskImage: "linear-gradient(to right, transparent 0, #000 30px)",
              }
            : undefined
        }
      >
        <div
          ref={scrollRef}
          className="h-9 overflow-x-auto overflow-y-hidden no-scrollbar"
        >
          <div ref={stripRef} className="flex items-center h-9 whitespace-nowrap" style={{ width: contentW || "max-content" }}>
            <ToolButton active={tool === "select"} testId="tool-select-btn" title="Select (V)" icon={MousePointer2} onClick={() => setTool("select")} />
          <div className="tool-divider" />
          <ToolButton active={tool === "rect"} testId="tool-rect-btn" title="Rectangle (R)" icon={Square} onClick={() => setTool("rect")} />
          <ToolButton active={tool === "circle"} testId="tool-circle-btn" title="Circle (C)" icon={Circle} onClick={() => setTool("circle")} />
          <ToolButton active={tool === "line"} testId="tool-line-btn" title="Line (L)" icon={Minus} onClick={() => setTool("line")} />
          <ToolButton active={tool === "arrow"} testId="tool-arrow-btn" title="Arrow (A)" icon={ArrowRight} onClick={() => setTool("arrow")} />
          <ToolButton active={tool === "polygon"} testId="tool-polygon-btn" title="Triangle" icon={Triangle} onClick={() => setTool("polygon")} />
          <ToolButton active={tool === "star"} testId="tool-star-btn" title="Star" icon={Star} onClick={() => setTool("star")} />
          <ToolButton active={tool === "text"} testId="tool-text-btn" title="Text (T)" icon={Type} onClick={() => setTool("text")} />
          <div className="tool-divider" />
          <ToolButton active={tool === "pen"} testId="tool-pen-btn" title="Pen — click to add anchors, Enter to commit" icon={PenTool} onClick={() => setTool("pen")} />
          <ToolButton active={tool === "pencil"} testId="tool-pencil-btn" title="Pencil — free draw" icon={Pencil} onClick={() => setTool("pencil")} />
          <div className="tool-divider" />
          <ToolButton testId="tool-play-btn" title={playing ? "暂停 / Pause (Space)" : "播放动画 / Play — 文字打字机·图形绘制 (Space)"} icon={playing ? Pause : Play} onClick={togglePlay} />
          <ToolButton testId="tool-replay-btn" title="重播动画 / Replay — 文字打字机·图形绘制生成" icon={RotateCcw} onClick={onReplay} />
          <div className="tool-divider" />
          <ToolButton testId="tool-undo-btn" title="Undo (Ctrl+Z)" icon={Undo2} onClick={onUndo} disabled={!canUndo} />
          <ToolButton testId="tool-redo-btn" title="Redo (Ctrl+Shift+Z)" icon={Redo2} onClick={onRedo} disabled={!canRedo} />
          <ToolButton testId="tool-clear-btn" title="Clear canvas" icon={Trash} onClick={onClear} />
          <div className="tool-divider" />
          <ToolButton testId="tool-recenter-btn" title="回到中心 / 聚焦内容 · Focus content (Home)" icon={LocateFixed} onClick={onRecenter} />
          <ToolButton testId="tool-settings-btn" title="Canvas settings" icon={Settings2} onClick={onOpenSettings} />
          <div className="tool-divider" />
          </div>
        </div>
      </motion.div>

      <motion.button
        onClick={toggleCollapsed}
        data-testid={collapsed ? "toolbar-expand-btn" : "toolbar-collapse-btn"}
        title={collapsed ? "Expand toolbar" : "Collapse toolbar"}
        className="h-9 shrink-0 flex items-center text-ink3 hover:text-ink"
        initial={false}
        animate={{
          width: collapsed ? CAPSULE_W : BTN_W,
          paddingLeft: collapsed ? 10 : 0,
          justifyContent: collapsed ? "flex-start" : "center",
        }}
        transition={btnTransition}
      >
        {collapsed ? <ChevronLeft className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
      </motion.button>
    </motion.div>
  );
}
